import { useEffect, useRef } from "react";

type OnChange = (index: number) => void;

export function useGelTabsOnChange(onChange: OnChange) {
  const selectedRef = useRef(0);
  const focusedRef = useRef(0);

  const getTitles = (root: Element) =>
    Array.from(root.querySelectorAll('[data-cy="gel-tab-title"]'));

  const getSelectedIndex = (root: Element) => {
    // Try to infer by visibility (works for both mouse & keyboard)
    const contents = Array.from(
      root.querySelectorAll('[data-cy="gel-tab-content"]')
    ) as HTMLElement[];

    let idx = contents.findIndex((el) => {
      const cs = getComputedStyle(el);
      const rect = el.getBoundingClientRect();
      return cs.display !== "none" && rect.width > 0 && rect.height > 0;
    });

    return idx < 0 ? selectedRef.current : idx;
  };

  const emit = (root: Element) => {
    const idx = getSelectedIndex(root);
    if (idx !== selectedRef.current) {
      selectedRef.current = idx;
      onChange(idx);
    }
  };

  // These handlers are attached to the *outer* DIV that GelTabs spreads {...props} onto
  const onFocusCapture = (e: React.FocusEvent<HTMLDivElement>) => {
    const root = e.currentTarget;
    const title = (e.target as Element).closest('[data-cy="gel-tab-title"]');
    if (!title) return;
    const idx = getTitles(root).indexOf(title as Element);
    if (idx >= 0) focusedRef.current = idx;
  };

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const root = e.currentTarget;
    // After GelTabs updates its internal state, emit the new index
    setTimeout(() => emit(root), 0);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const root = e.currentTarget;
    // Arrow keys / Space / Enter are handled by GelTabs—just emit after it updates
    if (["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", " ", "Enter"].includes(e.key)) {
      setTimeout(() => emit(root), 0);
    }
  };

  // Optional: fire once on mount (initially selected tab = 0)
  useEffect(() => {
    // Defer to next tick so the DOM is ready
    const t = setTimeout(() => {
      const anyRoot = document.querySelector('[data-cy="gel-tab-title"]')?.closest("div");
      if (anyRoot) emit(anyRoot);
    }, 0);
    return () => clearTimeout(t);
  }, []);

  return { onClick, onKeyDown, onFocusCapture };
}